/**
 * Drive to Store (DTS) — 3-Step Wizard
 *
 * Step 1: Google Maps store selection (DTS-enabled stores only)
 * Step 2: Calendar date picker (pure JS, no library)
 * Step 3: Customer form + submission
 *
 * Google Maps JavaScript API + DTS App Proxy API
 * Uses AdvancedMarkerElement + PinElement (google.maps.Marker deprecated since feb 2024)
 * Uses PlaceAutocompleteElement (new API since march 2025)
 */

(function () {
  'use strict';

  // ── Config ───────────────────────────────────────────────────────────────
  // Cambiar a false cuando la app este en Azure
  const DTS_MOCK_MODE = false;

  const DTS_LIMITS = { MAX_OPTICAL: 3, MAX_SUN: 1 };

  const COUNTRY_CENTERS = {
    ES: { lat: 40.4168, lng: -3.7038, zoom: 6 },
    FR: { lat: 48.8534, lng: 2.3488, zoom: 6 },
    DE: { lat: 52.52, lng: 13.405, zoom: 6 },
    IT: { lat: 41.9028, lng: 12.4964, zoom: 6 },
    US: { lat: 37.0902, lng: -95.7129, zoom: 4 },
    GB: { lat: 52.3555, lng: -1.1743, zoom: 6 },
    PT: { lat: 39.3999, lng: -8.2245, zoom: 7 },
    BE: { lat: 50.5039, lng: 4.4699, zoom: 7 },
    NL: { lat: 52.1326, lng: 5.2913, zoom: 7 },
    CA: { lat: 56.1304, lng: -106.3468, zoom: 4 },
    GR: { lat: 39.0742, lng: 21.8243, zoom: 7 },
  };

  // ── State ────────────────────────────────────────────────────────────────

  let map;
  let markers = [];
  let clusterer;
  let infoWindow;
  let currentFeatures = [];
  let storesCache = {};

  let state = {
    currentStep: 1,
    selectedStore: null,
    selectedDate: null,
    calendarMonth: new Date().getMonth(),
    calendarYear: new Date().getFullYear(),
    cartItems: [],
  };

  // ── Init ──────────────────────────────────────────────────────────────────

  function init() {
    const container = document.querySelector('[data-ff-dts]');
    if (!container) return;

    // Read cart data from JSON script tag
    const cartDataEl = document.querySelector('[data-ff-dts-cart-data]');
    if (cartDataEl) {
      try {
        state.cartItems = JSON.parse(cartDataEl.textContent);
      } catch (err) {
        console.error('[DTS] Error parsing cart data:', err);
        state.cartItems = [];
      }
    }

    initMap();
    initCalendar();
    initSteps();
    initForm();
  }

  // Espera a que Google Maps cargue, luego inicializa
  function waitForGoogleMaps() {
    if (typeof google !== 'undefined' && google.maps && google.maps.Map) {
      init();
    } else {
      // Google Maps aun no ha cargado — reintentar
      setTimeout(waitForGoogleMaps, 100);
    }
  }

  // Arrancar cuando el DOM este listo
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForGoogleMaps);
  } else {
    waitForGoogleMaps();
  }

  // ── Map (Step 1) ─────────────────────────────────────────────────────────

  function initMap() {
    const mapEl = document.getElementById('ff-dts-map');
    if (!mapEl) return;

    // En mock mode sin Google Maps disponible, mostrar óptica mock directamente
    if (DTS_MOCK_MODE && (typeof google === 'undefined' || !google.maps)) {
      console.warn('[DTS] Google Maps not available — showing mock store selector');
      mapEl.style.display = 'none';
      const searchEl = document.getElementById('ff-dts-search');
      if (searchEl) searchEl.style.display = 'none';
      renderMockStoreList();
      return;
    }

    const mapId = mapEl.dataset.mapId || '';

    const mapOptions = {
      center: { lat: 41.3851, lng: 2.1734 },
      zoom: 12,
      mapTypeControl: false,
      fullscreenControl: false,
      streetViewControl: false,
    };
    if (mapId) mapOptions.mapId = mapId;

    map = new google.maps.Map(mapEl, mapOptions);
    infoWindow = new google.maps.InfoWindow();

    // Recargar stores cada vez que el mapa deja de moverse
    map.addListener('idle', loadStores);

    initSearch();
  }

  function renderMockStoreList() {
    const storeCardContainer = document.querySelector('[data-ff-dts-store-card]');
    if (!storeCardContainer) return;

    const mockStores = [
      { hash: 'QzAwMQ', name: 'Óptica Looley Madrid Centro', street: 'Calle Gran Vía 42, Planta 2', city: 'Madrid', zip: '28013', country: 'ES' },
      { hash: 'QzAwNA', name: 'Óptica Barcelona Eixample', street: 'Carrer del Consell de Cent 310, Local B', city: 'Barcelona', zip: '08007', country: 'ES' },
      { hash: 'QzAwNg', name: 'Optique Marais', street: '14 Rue des Francs-Bourgeois', city: 'Paris', zip: '75003', country: 'FR' },
      { hash: 'QzAwOQ', name: 'Brillen Berlin Mitte', street: 'Unter den Linden 12', city: 'Berlin', zip: '10117', country: 'DE' },
    ];

    const listHTML = mockStores.map((store) => `
      <div class="ff-dts__mock-store" data-mock-hash="${escapeAttr(store.hash)}" style="padding: 16px; border: 1px solid #ddd; border-radius: 8px; cursor: pointer; margin-bottom: 8px;">
        <p style="font-weight: 600; margin: 0 0 4px;">${escapeHTML(store.name)}</p>
        <p style="margin: 0; font-size: 14px; color: #555;">${escapeHTML(store.street)}<br>${escapeHTML(store.zip)} ${escapeHTML(store.city)}, ${escapeHTML(store.country)}</p>
      </div>
    `).join('');

    storeCardContainer.innerHTML = `<p style="font-size: 14px; color: #666; margin-bottom: 12px;">[Mock mode] Selecciona una óptica:</p>${listHTML}`;
    storeCardContainer.style.display = '';

    storeCardContainer.querySelectorAll('.ff-dts__mock-store').forEach((el) => {
      el.addEventListener('click', () => {
        const store = mockStores.find((s) => s.hash === el.dataset.mockHash);
        if (!store) return;
        state.selectedStore = {
          hash: store.hash,
          name: store.name,
          street: store.street,
          city: store.city,
          zip: store.zip,
          country: store.country,
        };
        completeStep(1);
      });
    });
  }

  // ── Load DTS stores by viewport ──────────────────────────────────────────

  async function loadStores() {
    const bounds = map.getBounds();
    if (!bounds) return;

    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();

    if (Math.abs(ne.lat() - sw.lat()) < 0.001 || Math.abs(ne.lng() - sw.lng()) < 0.001) {
      console.warn('[DTS] Invalid bounds, skipping load');
      return;
    }

    const bbox = [sw.lng(), sw.lat(), ne.lng(), ne.lat()].join(',');

    try {
      const allFeatures = DTS_MOCK_MODE
        ? getMockStores()
        : await fetch(`/apps/api/shopfinder/stores?bbox=${bbox}`).then((r) => {
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            return r.json();
          });

      currentFeatures = allFeatures.filter((f) => f.properties.hasDTS === 'Y');
      renderMarkers(currentFeatures);
    } catch (err) {
      console.error('[DTS] Error loading stores:', err);
    }
  }

  // ── Render markers ───────────────────────────────────────────────────────
  // Usa AdvancedMarkerElement si hay mapId, google.maps.Marker si no

  function renderMarkers(features) {
    // Limpiar markers anteriores
    markers.forEach((m) => {
      if (m.setMap) m.setMap(null);
      else if (m.map !== undefined) m.map = null;
    });
    markers = [];

    features.forEach((feature) => {
      const lng = parseFloat(feature.geometry.coordinates[0]);
      const lat = parseFloat(feature.geometry.coordinates[1]);
      const { hash } = feature.properties;

      const pinEl = document.createElement('div');
      pinEl.style.cssText = 'width:24px;height:24px;background:#1a1a1a;border:2px solid #fff;border-radius:50%;cursor:pointer;box-shadow:0 2px 6px rgba(0,0,0,0.3);';
      pinEl.dataset.dtsHash = hash;

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat, lng },
        map: map,
        content: pinEl,
      });

      // Click en el div directamente
      pinEl.addEventListener('click', (e) => {
        e.stopPropagation();
        onMarkerClick(hash, marker);
      });

      markers.push(marker);
    });
  }

  // ── Marker click → store detail card (fetch + cache) ─────────────────────

  async function onMarkerClick(hash, marker) {
    try {
      // Si ya lo tenemos cacheado, no hacer fetch
      if (!storesCache[hash]) {
        const detail = DTS_MOCK_MODE
          ? getMockStoreDetail(hash)
          : await fetch(`/apps/api/shopfinder/stores/${encodeURIComponent(hash)}`).then((r) => {
              if (!r.ok) throw new Error(`HTTP ${r.status}`);
              return r.json();
            });

        if (!detail) {
          console.warn('[DTS] No store found for hash:', hash);
          return;
        }
        storesCache[hash] = detail;
      }

      const store = storesCache[hash];
      const pos = marker.position;
      if (pos) map.panTo(pos);

      renderStoreCard(hash, store);
    } catch (err) {
      console.error('[DTS] Error loading store detail:', err);
    }
  }

  function renderStoreCard(hash, store) {
    const cardContainer = document.querySelector('[data-ff-dts-store-card]');
    if (!cardContainer) return;

    cardContainer.innerHTML = `
      <div class="ff-dts__store-card-info">
        <p class="ff-dts__store-card-name ff-snippet-text"><strong>${escapeHTML(store.name)}</strong></p>
        <p class="ff-dts__store-card-address ff-snippet-text">
          ${escapeHTML(store.street)}<br>
          ${escapeHTML(store.zip)} ${escapeHTML(store.city)}, ${escapeHTML(store.country)}
        </p>
      </div>
      <button
        class="ff-snippet-button ff-dts-action-btn"
        type="button"
        data-ff-dts-confirm="1"
        data-store-hash="${escapeAttr(hash)}"
        data-store-name="${escapeAttr(store.name)}"
        data-store-street="${escapeAttr(store.street)}"
        data-store-city="${escapeAttr(store.city)}"
        data-store-zip="${escapeAttr(store.zip)}"
        data-store-country="${escapeAttr(store.country)}"
      >
        <span class="ff-button-text">
          <div class="ff-dts-action-btn-text ff-snippet-text">
            ${escapeHTML(getTranslation('confirmStore'))}
          </div>
        </span>
      </button>
    `;

    cardContainer.style.display = '';

    // Bind confirm button
    const confirmBtn = cardContainer.querySelector('[data-ff-dts-confirm="1"]');
    if (confirmBtn) {
      confirmBtn.addEventListener('click', () => {
        state.selectedStore = {
          hash: confirmBtn.dataset.storeHash,
          name: confirmBtn.dataset.storeName,
          street: confirmBtn.dataset.storeStreet,
          city: confirmBtn.dataset.storeCity,
          zip: confirmBtn.dataset.storeZip,
          country: confirmBtn.dataset.storeCountry,
        };
        completeStep(1);
      });
    }
  }

  // ── Search (PlaceAutocompleteElement) ────────────────────────────────────

  function initSearch() {
    const input = document.getElementById('ff-dts-search-input');
    if (!input) return;

    const autocomplete = new google.maps.places.Autocomplete(input, {
      types: ['geocode'],
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place.geometry) return;

      if (place.geometry.viewport) {
        map.fitBounds(place.geometry.viewport);
      } else if (place.geometry.location) {
        const type = place.types?.[0];
        const zoom = type === 'country' ? 5
          : type === 'postal_code' ? 13
          : 11;
        map.panTo(place.geometry.location);
        map.setZoom(zoom);
      }
    });
  }

  // ── Near me button ───────────────────────────────────────────────────────

  function initNearMe() {
    const btn = document.querySelector('[data-ff-dts-near-me]');
    if (!btn || !navigator.geolocation) {
      if (btn) btn.style.display = 'none';
      return;
    }

    btn.addEventListener('click', () => {
      btn.disabled = true;
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          map.panTo({ lat: coords.latitude, lng: coords.longitude });
          map.setZoom(12);
          btn.disabled = false;
        },
        () => {
          console.warn('[DTS] Geolocation permission denied');
          btn.disabled = false;
        }
      );
    });
  }

  // ── Calendar (Step 2) ────────────────────────────────────────────────────

  function initCalendar() {
    const calendarEl = document.querySelector('[data-ff-dts-calendar]');
    if (!calendarEl) return;

    renderCalendar();

    // Month navigation
    calendarEl.addEventListener('click', (e) => {
      if (e.target.closest('[data-ff-dts-calendar-prev]')) {
        navigateMonth(-1);
      } else if (e.target.closest('[data-ff-dts-calendar-next]')) {
        navigateMonth(1);
      } else if (e.target.closest('.ff-dts__calendar-day:not(.ff-dts__calendar-day--disabled)')) {
        const dayEl = e.target.closest('.ff-dts__calendar-day');
        selectDay(dayEl);
      }
    });
  }

  function renderCalendar() {
    const calendarEl = document.querySelector('[data-ff-dts-calendar]');
    if (!calendarEl) return;

    const { calendarMonth, calendarYear } = state;
    const locale = document.documentElement.lang || 'es';
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Month name (capitalized)
    const monthFormatter = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' });
    const monthName = monthFormatter.format(new Date(calendarYear, calendarMonth, 1));
    const capitalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);

    // Day names (short)
    const dayFormatter = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    const dayNames = [];
    for (let i = 0; i < 7; i++) {
      // Start from Monday (day index 1)
      const date = new Date(2024, 0, i + 1); // 2024-01-01 is a Monday
      const dayName = dayFormatter.format(date);
      dayNames.push(dayName.charAt(0).toUpperCase() + dayName.slice(1));
    }

    // First day of month and total days
    const firstDay = new Date(calendarYear, calendarMonth, 1);
    const lastDay = new Date(calendarYear, calendarMonth + 1, 0);
    const totalDays = lastDay.getDate();

    // Day of week offset (Monday = 0)
    let startOffset = firstDay.getDay() - 1;
    if (startOffset < 0) startOffset = 6; // Sunday wraps to 6

    // Can navigate back?
    const isCurrentMonth = calendarYear === today.getFullYear() && calendarMonth === today.getMonth();

    // Build day cells
    let dayCells = '';

    // Previous month trailing days
    const prevMonthLastDay = new Date(calendarYear, calendarMonth, 0).getDate();
    for (let i = startOffset - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      dayCells += `<div class="ff-dts__calendar-day ff-dts__calendar-day--outside ff-dts__calendar-day--disabled">${day}</div>`;
    }

    // Current month days
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(calendarYear, calendarMonth, day);
      date.setHours(0, 0, 0, 0);

      const isPast = date < today;
      const isToday = date.getTime() === today.getTime();
      const dateStr = formatDateISO(calendarYear, calendarMonth + 1, day);
      const isSelected = state.selectedDate === dateStr;

      let classes = 'ff-dts__calendar-day';
      if (isPast) classes += ' ff-dts__calendar-day--disabled';
      if (isToday) classes += ' ff-dts__calendar-day--today';
      if (isSelected) classes += ' ff-dts__calendar-day--selected';

      dayCells += `<div class="${classes}" data-date="${escapeAttr(dateStr)}">${day}</div>`;
    }

    // Next month leading days to complete the last week
    const totalCells = startOffset + totalDays;
    const remaining = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);
    for (let i = 1; i <= remaining; i++) {
      dayCells += `<div class="ff-dts__calendar-day ff-dts__calendar-day--outside ff-dts__calendar-day--disabled">${i}</div>`;
    }

    calendarEl.innerHTML = `
      <div class="ff-dts__calendar-header">
        <button
          class="ff-dts__calendar-nav"
          type="button"
          data-ff-dts-calendar-prev
          ${isCurrentMonth ? 'disabled' : ''}
          aria-label="Previous month"
        >&lt;</button>
        <span class="ff-dts__calendar-month">${escapeHTML(capitalizedMonth)}</span>
        <button
          class="ff-dts__calendar-nav"
          type="button"
          data-ff-dts-calendar-next
          aria-label="Next month"
        >&gt;</button>
      </div>
      <div class="ff-dts__calendar-grid">
        ${dayNames.map((d) => `<div class="ff-dts__calendar-day-name">${escapeHTML(d)}</div>`).join('')}
        ${dayCells}
      </div>
    `;
  }

  function navigateMonth(direction) {
    state.calendarMonth += direction;
    if (state.calendarMonth > 11) {
      state.calendarMonth = 0;
      state.calendarYear += 1;
    } else if (state.calendarMonth < 0) {
      state.calendarMonth = 11;
      state.calendarYear -= 1;
    }

    // No navegar antes del mes actual
    const today = new Date();
    if (state.calendarYear < today.getFullYear()
      || (state.calendarYear === today.getFullYear() && state.calendarMonth < today.getMonth())) {
      state.calendarMonth = today.getMonth();
      state.calendarYear = today.getFullYear();
    }

    renderCalendar();
  }

  function selectDay(dayEl) {
    const calendarEl = document.querySelector('[data-ff-dts-calendar]');
    if (!calendarEl) return;

    // Remove previous selection
    const prev = calendarEl.querySelector('.ff-dts__calendar-day--selected');
    if (prev) prev.classList.remove('ff-dts__calendar-day--selected');

    // Set new selection
    dayEl.classList.add('ff-dts__calendar-day--selected');
    state.selectedDate = dayEl.dataset.date;

    // Enable confirm button
    const confirmBtn = document.querySelector('[data-ff-dts-confirm="2"]');
    if (confirmBtn) confirmBtn.disabled = false;
  }

  function formatDateISO(year, month, day) {
    const m = String(month).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${year}-${m}-${d}`;
  }

  function formatDateDisplay(isoDate) {
    if (!isoDate) return '';
    const [year, month, day] = isoDate.split('-');
    return `${day}-${month}-${year}`;
  }

  // ── Steps Navigation ─────────────────────────────────────────────────────

  function initSteps() {
    const container = document.querySelector('[data-ff-dts]');
    if (!container) return;

    // Confirm step 2 (calendar)
    container.addEventListener('click', (e) => {
      const confirmBtn = e.target.closest('[data-ff-dts-confirm="2"]');
      if (confirmBtn && state.selectedDate) {
        completeStep(2);
        return;
      }

      const modifyBtn1 = e.target.closest('[data-ff-dts-modify="1"]');
      if (modifyBtn1) {
        editStep(1);
        return;
      }

      const modifyBtn2 = e.target.closest('[data-ff-dts-modify="2"]');
      if (modifyBtn2) {
        editStep(2);
        return;
      }

      const submitBtn = e.target.closest('[data-ff-dts-submit]');
      if (submitBtn) {
        handleSubmit();
        return;
      }
    });

    // Set initial step state
    goToStep(1);
  }

  function goToStep(stepNum) {
    state.currentStep = stepNum;
    const container = document.querySelector('[data-ff-dts]');
    if (!container) return;

    for (let i = 1; i <= 3; i++) {
      const stepEl = container.querySelector(`[data-ff-dts-step="${i}"]`);
      if (!stepEl) continue;

      const contentEl = stepEl.querySelector('[data-ff-dts-step-content]');
      const summaryEl = stepEl.querySelector('[data-ff-dts-step-summary]');

      if (i === stepNum) {
        // Active step
        stepEl.classList.add('ff-dts__step--active');
        stepEl.classList.remove('ff-dts__step--completed', 'ff-dts__step--disabled');
        if (contentEl) contentEl.style.display = '';
        if (summaryEl) summaryEl.style.display = 'none';
      } else if (i < stepNum) {
        // Completed steps stay as completed
        stepEl.classList.add('ff-dts__step--completed');
        stepEl.classList.remove('ff-dts__step--active', 'ff-dts__step--disabled');
        if (contentEl) contentEl.style.display = 'none';
        if (summaryEl) summaryEl.style.display = '';
      } else {
        // Future steps are disabled
        stepEl.classList.add('ff-dts__step--disabled');
        stepEl.classList.remove('ff-dts__step--active', 'ff-dts__step--completed');
        if (contentEl) contentEl.style.display = 'none';
        if (summaryEl) summaryEl.style.display = 'none';
      }
    }
  }

  function completeStep(stepNum) {
    const container = document.querySelector('[data-ff-dts]');
    if (!container) return;

    const stepEl = container.querySelector(`[data-ff-dts-step="${stepNum}"]`);
    if (!stepEl) return;

    // Update summary with selected data
    const summaryEl = stepEl.querySelector('[data-ff-dts-step-summary]');
    if (summaryEl) {
      if (stepNum === 1 && state.selectedStore) {
        summaryEl.innerHTML = `
          <div class="ff-dts__step-summary-content">
            <div class="ff-dts__step-summary-info">
              <p class="ff-dts__step-summary-title ff-dts__store-card-name ff-snippet-text">${escapeHTML(state.selectedStore.name)}</p>
              <p class="ff-dts__step-summary-detail ff-dts__store-card-address ff-snippet-text">
                ${escapeHTML(state.selectedStore.street)}, ${escapeHTML(state.selectedStore.zip)} ${escapeHTML(state.selectedStore.city)}
              </p>
            </div>
            <button class="ff-snippet-button ff-dts__step-modify" type="button" data-ff-dts-modify="1">
              <span class="ff-button-text">
                <div class="ff-dts__step-modify-text ff-snippet-text">
                  ${escapeHTML(getTranslation('modify'))}
                </div>
              </span>
            </button>
          </div>
        `;
      } else if (stepNum === 2 && state.selectedDate) {
        summaryEl.innerHTML = `
          <div class="ff-dts__step-summary-content">
            <div class="ff-dts__step-summary-info">
              <p class="ff-dts__step-summary-title ff-dts__store-card-name ff-snippet-text">${escapeHTML(formatDateDisplay(state.selectedDate))}</p>
            </div>
            <button class="ff-snippet-button ff-dts__step-modify" type="button" data-ff-dts-modify="2">
              <span class="ff-button-text">
                <div class="ff-dts__step-modify-text ff-snippet-text">
                  ${escapeHTML(getTranslation('modify'))}
                </div>
              </span>
            </button>
          </div>
        `;
      }
    }

    // Go to next step
    const nextStep = stepNum + 1;
    if (nextStep <= 3) {
      goToStep(nextStep);
    }
  }

  function editStep(stepNum) {
    // Reset steps after the one being edited
    if (stepNum <= 1) {
      state.selectedStore = null;
      const storeCard = document.querySelector('[data-ff-dts-store-card]');
      if (storeCard) {
        storeCard.innerHTML = '';
        storeCard.style.display = 'none';
      }
    }
    if (stepNum <= 2) {
      state.selectedDate = null;
      const confirmBtn = document.querySelector('[data-ff-dts-confirm="2"]');
      if (confirmBtn) confirmBtn.disabled = true;
    }

    goToStep(stepNum);

    // Re-render calendar to clear selection
    if (stepNum <= 2) {
      renderCalendar();
    }
  }

  // ── Form (Step 3) ────────────────────────────────────────────────────────

  function initForm() {
    const form = document.querySelector('[data-ff-dts-form]');
    if (!form) return;

    // Prevent native submission
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      handleSubmit();
    });
  }

  function validateForm() {
    const form = document.querySelector('[data-ff-dts-form]');
    if (!form) return { valid: false, errors: [] };

    const errors = [];

    const firstName = form.querySelector('[name="firstName"]');
    const lastName = form.querySelector('[name="lastName"]');
    const email = form.querySelector('[name="email"]');
    const phone = form.querySelector('[name="phone"]');
    const privacyPolicy = form.querySelector('[name="privacyPolicyAccepted"]');

    // Required fields
    if (!firstName || !firstName.value.trim()) {
      errors.push({ field: 'firstName', message: getTranslation('errorRequired') });
    }
    if (!lastName || !lastName.value.trim()) {
      errors.push({ field: 'lastName', message: getTranslation('errorRequired') });
    }
    if (!email || !email.value.trim()) {
      errors.push({ field: 'email', message: getTranslation('errorRequired') });
    } else if (!isValidEmail(email.value.trim())) {
      errors.push({ field: 'email', message: getTranslation('errorEmail') });
    }
    if (!privacyPolicy || !privacyPolicy.checked) {
      errors.push({ field: 'privacyPolicyAccepted', message: getTranslation('errorPrivacy') });
    }

    return { valid: errors.length === 0, errors };
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function getFormData() {
    const form = document.querySelector('[data-ff-dts-form]');
    if (!form) return null;

    return {
      firstName: (form.querySelector('[name="firstName"]')?.value || '').trim(),
      lastName: (form.querySelector('[name="lastName"]')?.value || '').trim(),
      email: (form.querySelector('[name="email"]')?.value || '').trim(),
      phone: (form.querySelector('[name="phone"]')?.value || '').trim(),
      newsletterConsent: form.querySelector('[name="newsletterConsent"]')?.checked || false,
      privacyPolicyAccepted: form.querySelector('[name="privacyPolicyAccepted"]')?.checked || false,
    };
  }

  function showFormErrors(errors) {
    const form = document.querySelector('[data-ff-dts-form]');
    if (!form) return;

    // Clear previous errors
    form.querySelectorAll('.ff-dts__form-error').forEach((el) => el.remove());
    form.querySelectorAll('.ff-dts__form-field--error').forEach((el) => {
      el.classList.remove('ff-dts__form-field--error');
    });

    errors.forEach(({ field, message }) => {
      const input = form.querySelector(`[name="${CSS.escape(field)}"]`);
      if (!input) return;

      const fieldWrapper = input.closest('.ff-dts__form-field');
      if (fieldWrapper) fieldWrapper.classList.add('ff-dts__form-field--error');

      const errorEl = document.createElement('span');
      errorEl.className = 'ff-dts__form-error';
      errorEl.textContent = message;

      const isCheckable = input.type === 'checkbox' || input.type === 'radio';
      const anchor = isCheckable ? (input.closest('.ff-dts__checkbox-label') || input) : input;
      anchor.parentNode.insertBefore(errorEl, anchor.nextSibling);
    });
  }

  function clearFormErrors() {
    const form = document.querySelector('[data-ff-dts-form]');
    if (!form) return;

    form.querySelectorAll('.ff-dts__form-error').forEach((el) => el.remove());
    form.querySelectorAll('.ff-dts__form-field--error').forEach((el) => {
      el.classList.remove('ff-dts__form-field--error');
    });
  }

  async function handleSubmit() {
    clearFormErrors();

    // Validate state
    if (!state.selectedStore) {
      console.error('[DTS] No store selected');
      return;
    }
    if (!state.selectedDate) {
      console.error('[DTS] No date selected');
      return;
    }

    const { valid, errors } = validateForm();
    if (!valid) {
      showFormErrors(errors);
      return;
    }

    const formData = getFormData();
    const payload = buildDTSPayload(formData);
    const submitBtn = document.querySelector('[data-ff-dts-submit]');
    const messageEl = document.querySelector('[data-ff-dts-message]');

    // Loading state
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.classList.add('ff-dts__submit--loading');
    }
    if (messageEl) {
      messageEl.innerHTML = '';
      messageEl.style.display = 'none';
    }

    try {
      await submitDTSRequest(payload);
      showSuccessPopup();
    } catch (err) {
      console.error('[DTS] Submission error:', err);

      if (messageEl) {
        messageEl.innerHTML = `
          <div class="ff-dts__message ff-dts__message--error">
            <p>${escapeHTML(err.message || getTranslation('errorSubmit'))}</p>
          </div>
        `;
        messageEl.style.display = '';
      }
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.classList.remove('ff-dts__submit--loading');
      }
    }
  }

  // ── Payload Builder ──────────────────────────────────────────────────────

  // Extracts caliber as a positive integer.
  // Priority: metafield (item.caliber) > SKU pattern (e.g. "9 TILE.A 53O HVGD") > option values.
  function parseCaliber(item) {
    const fromMetafield = parseInt(item.caliber, 10);
    if (Number.isInteger(fromMetafield) && fromMetafield > 0) return fromMetafield;

    const fromSku = item.sku && item.sku.match(/\b(\d{2,3})[OS]\b/);
    if (fromSku) return parseInt(fromSku[1], 10);

    const optionMatch = (item.options || [])
      .map((o) => String(o.value || '').match(/(\d{2,3})/))
      .find(Boolean);
    if (optionMatch) return parseInt(optionMatch[1], 10);

    return null;
  }

  // Maps Shopify product type to DTS type ("optical" | "sun").
  // Source of truth is the SKU letter after caliber: "53O" = optical, "53S" = sun.
  function parseDTSType(item) {
    if (item.sku && /\b\d{2,3}O\b/.test(item.sku)) return 'optical';
    if (item.sku && /\b\d{2,3}S\b/.test(item.sku)) return 'sun';
    const t = String(item.type || '').toLowerCase();
    if (t.includes('sun')) return 'sun';
    return 'optical';
  }

  function buildDTSPayload(formData) {
    return {
      customer: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone || undefined,
        newsletterConsent: formData.newsletterConsent,
        privacyPolicyAccepted: formData.privacyPolicyAccepted,
      },
      lang: (document.documentElement.lang || 'en').split('-')[0],
      storeHash: state.selectedStore.hash,
      trialDate: state.selectedDate,
      items: state.cartItems.map((item) => ({
        title: item.title,
        handle: item.handle,
        sku: item.sku,
        caliber: parseCaliber(item),
        type: parseDTSType(item),
        imageUrl: item.imageUrl || undefined,
      })),
    };
  }

  // ── Success Popup ────────────────────────────────────────────────────────

  function showSuccessPopup() {
    const store = state.selectedStore || {};
    const date = state.selectedDate ? new Date(state.selectedDate) : null;
    const items = state.cartItems || [];

    const refLabel = document.querySelector('[data-ff-dts]')?.dataset.tRefLabel || 'Ref.';

    const storeEl = document.querySelector('[data-ff-dts-success-store]');
    if (storeEl) {
      const addrLine = [store.zip, store.city].filter(Boolean).join(' ');
      storeEl.innerHTML = `
        <p class="ff-dts__store-card-name"><strong>${escapeHTML(store.name || '')}</strong></p>
        ${store.street ? `<p>${escapeHTML(store.street)}</p>` : ''}
        ${addrLine ? `<p>${escapeHTML(addrLine)}${store.country ? `, ${escapeHTML(store.country)}` : ''}</p>` : ''}
      `;
    }

    const dateEl = document.querySelector('[data-ff-dts-success-date]');
    if (dateEl && date) {
      dateEl.textContent = date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }

    const itemsEl = document.querySelector('[data-ff-dts-success-items]');
    if (itemsEl) {
      itemsEl.innerHTML = items.map((item) => `
        <div class="ff-dts__sidebar-item">
          <div class="ff-dts__sidebar-item-image">
            ${item.imageUrl ? `<img src="${escapeAttr(item.imageUrl)}" alt="${escapeAttr(item.title)}" loading="lazy" />` : ''}
          </div>
          <div class="ff-dts__sidebar-item-info">
            <p class="ff-dts__sidebar-item-title">${escapeHTML(item.title)}</p>
            <p class="ff-dts__sidebar-item-ref">${escapeHTML(refLabel)} ${escapeHTML(item.sku)}</p>
          </div>
        </div>
      `).join('');
    }

    document.dispatchEvent(new CustomEvent('ff-popup:open', {
      detail: { popupId: 'ff-dts-success-popup' }
    }));

    wireSuccessCTA();
  }

  let ctaWired = false;
  function wireSuccessCTA() {
    if (ctaWired) return;
    ctaWired = true;
    const cta = document.querySelector('[data-ff-dts-cta]');
    if (!cta) return;
    cta.addEventListener('click', async (e) => {
      e.preventDefault();
      cta.style.pointerEvents = 'none';
      cta.style.opacity = '0.6';
      try {
        // Clear cart so a returning user starts a fresh DTS flow.
        await fetch('/cart/clear.js', { method: 'POST' });
      } catch (err) {
        console.error('[DTS] Could not clear cart:', err);
      }
      window.location.href = cta.getAttribute('href') || '/';
    });
  }

  // ── API Functions ────────────────────────────────────────────────────────

  async function submitDTSRequest(payload) {
    if (DTS_MOCK_MODE) {
      return mockSubmitDTSRequest(payload);
    }

    const res = await fetch('/apps/api/dts/request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to submit');
    return data;
  }

  // ── Translations ─────────────────────────────────────────────────────────

  function getTranslation(key) {
    const container = document.querySelector('[data-ff-dts]');
    if (!container) return key;

    const translations = {
      confirmStore: container.dataset.tStep1Confirm || 'CONFIRM STORE',
      confirmDate: container.dataset.tStep2Confirm || 'CONFIRM DATE',
      modify: container.dataset.tModify || 'Modify',
      submit: container.dataset.tStep3Submit || 'FINALIZE BOOKING',
      successTitle: container.dataset.tSuccessTitle || 'Request sent!',
      successMessage: container.dataset.tSuccessMessage || 'You will receive a confirmation email shortly.',
      errorSubmit: container.dataset.tErrorSubmit || 'Could not send the request. Please try again.',
      errorRequired: container.dataset.tErrorRequired || 'Please complete all required fields.',
      errorEmail: container.dataset.tErrorEmail || 'Please enter a valid email address.',
      errorPrivacy: container.dataset.tErrorPrivacy || 'You must accept the privacy policy.',
      errorNoStore: container.dataset.tErrorNoStore || 'Please select a store.',
      errorNoDate: container.dataset.tErrorNoDate || 'Please select a trial date.',
    };

    return translations[key] || key;
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  function escapeHTML(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function escapeAttr(str) {
    return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // ── Mock data ────────────────────────────────────────────────────────────

  function getMockStores() {
    return [
      // Espana — Madrid
      { type: 'Feature', geometry: { type: 'Point', coordinates: ['-3.7029', '40.4254'] }, properties: { hasDTS: 'Y', TipoABC: '1', hash: 'QzAwMQ' } },
      { type: 'Feature', geometry: { type: 'Point', coordinates: ['-3.6918', '40.4189'] }, properties: { hasDTS: '', TipoABC: '1', hash: 'QzAwMg' } },
      { type: 'Feature', geometry: { type: 'Point', coordinates: ['-3.7156', '40.4073'] }, properties: { hasDTS: '', TipoABC: '1', hash: 'QzAwMw' } },
      // Espana — Barcelona
      { type: 'Feature', geometry: { type: 'Point', coordinates: ['2.1734', '41.3851'] }, properties: { hasDTS: 'Y', TipoABC: '1', hash: 'QzAwNA' } },
      { type: 'Feature', geometry: { type: 'Point', coordinates: ['2.1540', '41.3780'] }, properties: { hasDTS: '', TipoABC: '1', hash: 'QzAwNQ' } },
      // Francia — Paris
      { type: 'Feature', geometry: { type: 'Point', coordinates: ['2.3200', '48.8700'] }, properties: { hasDTS: 'Y', TipoABC: '1', hash: 'QzAwNg' } },
      { type: 'Feature', geometry: { type: 'Point', coordinates: ['2.3488', '48.8534'] }, properties: { hasDTS: '', TipoABC: '1', hash: 'QzAwNw' } },
      // Francia — Lyon
      { type: 'Feature', geometry: { type: 'Point', coordinates: ['4.8357', '45.7640'] }, properties: { hasDTS: '', TipoABC: '1', hash: 'QzAwOA' } },
      // Alemania — Berlin
      { type: 'Feature', geometry: { type: 'Point', coordinates: ['13.4050', '52.5200'] }, properties: { hasDTS: 'Y', TipoABC: '1', hash: 'QzAwOQ' } },
      // Italia — Milan
      { type: 'Feature', geometry: { type: 'Point', coordinates: ['9.1900', '45.4654'] }, properties: { hasDTS: '', TipoABC: '1', hash: 'QzAxMA' } },
    ];
  }

  function getMockStoreDetail(hash) {
    const stores = {
      QzAwMQ: { name: 'Optica Looley Madrid Centro', street: 'Calle Gran Via 42, Planta 2', city: 'Madrid', zip: '28013', country: 'ES' },
      QzAwNA: { name: 'Optica Barcelona Eixample', street: 'Carrer del Consell de Cent 310, Local B', city: 'Barcelona', zip: '08007', country: 'ES' },
      QzAwNg: { name: 'Optique Marais', street: '14 Rue des Francs-Bourgeois', city: 'Paris', zip: '75003', country: 'FR' },
      QzAwOQ: { name: 'Brillen Berlin Mitte', street: 'Unter den Linden 12', city: 'Berlin', zip: '10117', country: 'DE' },
    };
    return stores[hash] ?? null;
  }

  function mockSubmitDTSRequest(payload) {
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log('[DTS] Mock submit payload:', payload);
        resolve({
          success: true,
          message: 'Your Drive to Store appointment has been confirmed. You will receive a confirmation email shortly.',
          confirmationId: 'DTS-' + Date.now(),
        });
      }, 800);
    });
  }
})();
